import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LinkIcon, LockIcon, MailIcon, UserIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  PasswordInput,
  ResultState,
} from '@opengoal/ui';
import { ApiError, normalizeAuthError } from '@opengoal/http';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { authClient } from '@/lib/auth-client';
import { setSessionToken } from '@/lib/session';
import { SignedInBanner } from '../components/SignedInBanner';

/**
 * Where `{INVITE_LINK_BASE_URL}/accept-invitation?invitationId=…` lands.
 *
 * Three calls, not one — the link carries no password, so an account has to
 * be created before the invitation can be redeemed against it:
 *
 *   1. `signUp.email` — gated server-side, and only succeeds for an address
 *      that actually holds a pending invitation. Returns no session since the
 *      backend turned on email verification (invited users are created
 *      verified, so nothing is sent to them).
 *   2. `signIn.email` — opens the session.
 *   3. `organization.acceptInvitation` — attaches the new user to the agency.
 *
 * The token from step 2 is the session token throughout. Step 3 updates that
 * same session in place (setting its active agency) and issues nothing new, so
 * it must be stored before step 3 runs and must not be replaced afterwards.
 *
 * Between the two calls the session exists but has no agency, and every
 * org-scoped endpoint answers `403 Active organization is required` — which is
 * why the profile query is only invalidated once both have succeeded.
 */
export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_public/accept-invitation' });
  const [error, setError] = useState<string | null>(null);

  const schema = z.object({
    name: z.string().trim().min(1, t('auth.nameRequired')),
    email: z.string().trim().min(1, t('auth.emailRequired')).email(t('auth.emailInvalid')),
    password: z.string().min(1, t('auth.choosePassword')).min(8, t('auth.passwordTooShort')),
  });
  type AcceptInvitationFormValues = z.infer<typeof schema>;

  const form = useForm<AcceptInvitationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const acceptInvitation = useMutation({
    mutationFn: async (values: AcceptInvitationFormValues) => {
      const invitationId = search.invitationId ?? '';

      const { error: signUpError } = await authClient.signUp.email({
        name: values.name,
        email: values.email,
        password: values.password,
      });
      if (signUpError) throw normalizeAuthError(signUpError, 403);

      // Sign-up no longer opens a session (the backend requires email
      // verification for self-signups; invited users are created verified),
      // so sign in explicitly to get the token.
      const { data, error: signInError } = await authClient.signIn.email({
        email: values.email,
        password: values.password,
      });
      if (signInError) throw normalizeAuthError(signInError, 401);
      if (!data?.token) throw new ApiError({ status: 500, message: t('auth.errors.generic') });

      // Store before step 2: acceptInvitation is an authenticated call, and the
      // fetch client reads the token from the session store.
      setSessionToken(data.token);

      const { error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId,
      });
      if (acceptError) throw normalizeAuthError(acceptError, 400);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      void navigate({ to: '/' });
    },
    onError: (mutationError: unknown) => {
      setError(messageFor(mutationError, t));
    },
  });

  const brand = <BrandMark name={env.appName} tagline={t('app.tagline')} />;

  if (!search.invitationId) {
    return (
      <AuthShell brand={brand} title={t('auth.acceptTokenMissingTitle')}>
        <ResultState
          tone="warning"
          icon={<LinkIcon />}
          description={t('auth.acceptTokenMissingBody')}
          className="px-0 py-2"
          action={
            <Button asChild>
              <Link to="/login">{t('auth.signIn')}</Link>
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      brand={brand}
      title={t('auth.acceptTitle')}
      subtitle={t('auth.acceptSubtitle')}
      aside={{ heading: t('auth.acceptAsideHeading'), body: t('auth.acceptAsideBody') }}
      footer={
        <Link to="/login" className="text-muted-foreground hover:text-foreground">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      <SignedInBanner />

      <Form {...form}>
        <form
          noValidate
          className="grid gap-5"
          onSubmit={form.handleSubmit((values) => {
            setError(null);
            acceptInvitation.mutate(values);
          })}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.fullName')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<UserIcon />}
                    autoComplete="name"
                    placeholder={t('auth.fullNamePlaceholder')}
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* The invitation is bound to one address, but sign-up needs it sent
              explicitly and there is no anonymous endpoint to look it up from the
              id. Typing a different address fails the server-side gate with
              "Registration is invitation-only", which the error map explains. */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<MailIcon />}
                    type="email"
                    autoComplete="email"
                    placeholder="you@agency.com"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="new-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error ? <Callout variant="destructive" title={error} /> : null}

          <Button type="submit" size="lg" className="w-full" loading={acceptInvitation.isPending}>
            {t('auth.acceptCta')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}

/**
 * Each failure here has a different remedy, so they get different copy.
 *
 * `code` is the stable identifier; message text is prose that changes between
 * better-auth releases and must not be branched on.
 */
function messageFor(error: unknown, t: (key: string) => string): string {
  if (!(error instanceof ApiError)) return t('auth.errors.generic');

  // The signup gate: the address holds no pending invitation. Usually means
  // they typed a different address from the one that received the email.
  if (error.status === 403 && error.code === 'FORBIDDEN') return t('auth.errors.notInvited');
  if (error.status === 403) return t('auth.errors.seatsFull');
  if (error.status === 422 || error.code === 'USER_ALREADY_EXISTS') {
    return t('auth.errors.emailTaken');
  }
  // Expired, cancelled, or already accepted — all 400 from acceptInvitation.
  if (error.status === 400) return t('auth.errors.invitationInvalid');

  return t('auth.errors.generic');
}
